import { useEffect, useState } from "react";
import { useClerk } from "@clerk/react";
import { Trophy, LogOut, Clock, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";

interface GuestLayoutProps {
  orgName: string | null;
  contractLabel: string | null;
  expiresAt: string | null;
  children: React.ReactNode;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "Expired";
  const days = Math.floor(ms / (24 * 3600 * 1000));
  if (days > 0) return `${days}d remaining`;
  const hours = Math.floor(ms / (3600 * 1000));
  if (hours > 0) return `${hours}h remaining`;
  const mins = Math.floor(ms / (60 * 1000));
  return `${mins}m remaining`;
}

export function GuestLayout({ orgName, contractLabel, expiresAt, children }: GuestLayoutProps) {
  const { signOut } = useClerk();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const remaining = expiresAt ? new Date(expiresAt).getTime() - now : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b bg-navy text-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gold flex items-center justify-center">
            <Trophy className="h-4 w-4 text-gold-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Scale className="h-4 w-4" />
              <span>Guest Legal Reviewer</span>
            </div>
            <div className="text-xs text-white/70 truncate">
              {contractLabel ?? "Contract review"}
              {orgName ? ` · for ${orgName}` : null}
            </div>
          </div>
          {remaining !== null ? (
            <div
              className="hidden sm:flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-white/10"
              title={`Expires ${expiresAt}`}
              data-testid="guest-expiry-countdown"
            >
              <Clock className="h-3 w-3" />
              <span>{formatRemaining(remaining)}</span>
            </div>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
            onClick={() => signOut({ redirectUrl: "/" })}
            data-testid="guest-sign-out"
          >
            <LogOut className="h-4 w-4 mr-1.5" />
            Sign out
          </Button>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto p-4 sm:p-6">{children}</main>
      <footer className="border-t py-3 text-center text-xs text-muted-foreground">
        Read-only contract review — your access is limited to this single contract.
      </footer>
    </div>
  );
}
